import type Quill from 'quill/core';
import { useEffect } from 'react';
import getBlotVerticalSibling from '../../getBlotVerticalSibling';
import { useQuill } from '../../QuillContext';
import { CELL_IDENTITY_ATTRIBUTE } from '../constants';
import moveCursorToNeighborLine from '../moveCursorToNeighborLine';
import { parseCellIdentity, stringifyCellIdentity } from '../utils';

const handleMoveOut = (
  quill: Quill,
  ancestorQuill: Quill,
  direction: 'up' | 'right' | 'down' | 'left',
) => {
  const cell = quill.root.closest(`[${CELL_IDENTITY_ATTRIBUTE}]`);
  const parentNode = cell?.closest('table');
  if (!cell || !parentNode) return;
  const blot = ancestorQuill.scroll.find(parentNode, true);
  if (!blot || !('focusCell' in blot && typeof blot.focusCell === 'function')) {
    return;
  }
  const rowCount = parentNode.querySelectorAll('tr').length;
  const columnCount = parentNode.querySelectorAll('tr:first-child td').length;
  const identity = cell.getAttribute(CELL_IDENTITY_ATTRIBUTE);
  if (!identity) return;
  const [row, col] = parseCellIdentity(identity);
  switch (direction) {
    case 'left':
      if (col > 0) {
        blot.focusCell(stringifyCellIdentity(row, col - 1), 'end');
      } else if (row > 0) {
        blot.focusCell(stringifyCellIdentity(row - 1, columnCount - 1), 'end');
      } else {
        moveCursorToNeighborLine(ancestorQuill, blot, 'up');
      }
      break;
    case 'right':
      if (col < columnCount - 1) {
        blot.focusCell(stringifyCellIdentity(row, col + 1));
      } else if (row < rowCount - 1) {
        blot.focusCell(stringifyCellIdentity(row + 1, 0));
      } else {
        moveCursorToNeighborLine(ancestorQuill, blot, 'down');
      }
      break;
    case 'up':
      if (row > 0) {
        blot.focusCell(stringifyCellIdentity(row - 1, col), 'end');
      } else {
        moveCursorToNeighborLine(ancestorQuill, blot, 'up');
      }
      break;
    case 'down':
      if (row < rowCount - 1) {
        blot.focusCell(stringifyCellIdentity(row + 1, col));
      } else {
        moveCursorToNeighborLine(ancestorQuill, blot, 'down');
      }
      break;
    default:
      break;
  }
};

const useCellKeyboardShortcuts = () => {
  const { quill, ancestorQuill } = useQuill();
  useEffect(() => {
    if (!quill || !ancestorQuill) return undefined;
    const tabBinding = {
      key: 'Tab',
      shiftKey: null,
      shortKey: false,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handler: (_range: any, context: any) => {
        if (context.format.list) return true;
        handleMoveOut(
          quill,
          ancestorQuill,
          context.event.shiftKey ? 'left' : 'right',
        );
        return false;
      },
    };
    const arrowLeftRightBinding = {
      key: ['ArrowLeft', 'ArrowRight'],
      collapsed: true,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handler: (range: any, context: any) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
        const left = context.event.key === 'ArrowLeft';
        const { index } = range;
        if ((left && index > 0) || (!left && index < quill.getLength() - 1)) {
          return true;
        }
        handleMoveOut(quill, ancestorQuill, left ? 'left' : 'right');
        return false;
      },
    };
    const arrowUpDownBinding = {
      key: ['ArrowUp', 'ArrowDown'],
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handler: (range: any, context: any) => {
        const { line, event } = context;
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
        const up = event.key === 'ArrowUp';
        if (
          (up && getBlotVerticalSibling(line, false)[1]) ||
          (!up && getBlotVerticalSibling(line, true)[1])
        ) {
          return true;
        }
        const selectionBounds = quill.selection.getBounds(range.index);
        if (!selectionBounds) return true;
        const direction = up ? 'top' : 'bottom';
        const diff = Math.abs(
          selectionBounds[direction] -
            line.domNode.getBoundingClientRect()[direction],
        );
        const lineHeight = parseInt(
          getComputedStyle(line.domNode).lineHeight,
          10,
        );
        if (diff > lineHeight) return true;
        handleMoveOut(quill, ancestorQuill, up ? 'up' : 'down');
        return false;
      },
    };
    const selectAllBinding = {
      key: 'a',
      shortKey: true,
      handler: () => {
        quill.setSelection(0, quill.getLength());
      },
    };

    // @ts-expect-error
    quill.keyboard.prependBinding(arrowLeftRightBinding);
    quill.keyboard.addBinding(arrowUpDownBinding);
    // @ts-expect-error
    quill.keyboard.prependBinding(tabBinding);
    quill.keyboard.addBinding(selectAllBinding);
    return () => {
      // @ts-expect-error
      quill.keyboard.removeBinding(arrowLeftRightBinding);
      // @ts-expect-error
      quill.keyboard.removeBinding(arrowUpDownBinding);
      // @ts-expect-error
      quill.keyboard.removeBinding(tabBinding);
      // @ts-expect-error
      quill.keyboard.removeBinding(selectAllBinding);
    };
  }, [quill, ancestorQuill]);
};

export default useCellKeyboardShortcuts;
